import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Loader2, CheckCircle, AlertCircle, TrendingUp, Eye, Zap, Target } from 'lucide-react';

export function LeadMagnet() {
  const [url, setUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const analyzeWebsite = async (e) => {
    e.preventDefault();
    
    if (!url) {
      setError('Lütfen bir URL girin');
      return;
    }

    // Validate URL
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
    } catch {
      setError('Geçerli bir URL girin');
      return;
    }

    setError('');
    setIsAnalyzing(true);
    setResults(null);

    // Simulate AI analysis (2-3 seconds)
    await new Promise(resolve => setTimeout(resolve, 2500));

    // Generate simulated results based on URL
    const domain = url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
    const randomScore = Math.floor(Math.random() * 30) + 45; // 45-75 arası
    
    setResults({
      domain,
      overallScore: randomScore,
      metrics: [
        { 
          name: 'Dönüşüm Potansiyeli', 
          score: Math.floor(Math.random() * 20) + 50,
          icon: Target,
          insight: 'CTA konumlandırması ve mesaj netliği iyileştirilebilir'
        },
        { 
          name: 'Kullanıcı Deneyimi', 
          score: Math.floor(Math.random() * 25) + 55,
          icon: Eye,
          insight: 'Sayfa yükleme hızı ve mobil uyumluluk optimize edilmeli'
        },
        { 
          name: 'Güven Sinyalleri', 
          score: Math.floor(Math.random() * 30) + 40,
          icon: CheckCircle,
          insight: 'Sosyal kanıt ve güvenlik badge\'leri eklenebilir'
        },
        { 
          name: 'Mesaj Mimarisi', 
          score: Math.floor(Math.random() * 25) + 45,
          icon: Zap,
          insight: 'Değer önerisi ilk 3 saniyede net iletilmiyor'
        }
      ],
      potentialGrowth: `+${Math.floor(Math.random() * 100) + 80}%`,
      recommendations: [
        'Hero bölümünde değer önerisini netleştirin',
        'Güven sinyallerini above-the-fold konumlandırın',
        'CTA butonlarına mikro-animasyon ekleyin',
        'Form alanlarını sadeleştirin'
      ]
    });

    setIsAnalyzing(false);
  };

  const resetAnalysis = () => {
    setUrl('');
    setResults(null);
    setError('');
  };

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-900/30 via-[#121212] to-pink-900/30 border border-violet-500/20 p-8 md:p-12">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50" />
      
      <div className="relative z-10">
        <AnimatePresence mode="wait">
          {!results ? (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="text-center mb-8">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-500/20 text-violet-400 rounded-full text-sm font-medium mb-4">
                  <Zap className="w-4 h-4" />
                  Ücretsiz AI Analizi
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Web Sitenizin Büyüme Potansiyelini Keşfedin
                </h3>
                <p className="text-zinc-400 max-w-xl mx-auto">
                  aifabric yapay zekası, web sitenizi 30 saniyede analiz eder ve 
                  dönüşüm fırsatlarını ortaya çıkarır.
                </p>
              </div>

              <form onSubmit={analyzeWebsite} className="max-w-xl mx-auto">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <input
                      type="text"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="siteniz.com"
                      className="w-full pl-12 pr-4 py-4 bg-zinc-900/80 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 transition-colors"
                      disabled={isAnalyzing}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isAnalyzing}
                    className="px-8 py-4 bg-gradient-to-r from-violet-500 to-pink-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analiz Ediliyor...
                      </>
                    ) : (
                      <>
                        Ücretsiz Analiz
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
                {error && (
                  <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </p>
                )}
              </form>

              <p className="text-center text-zinc-500 text-xs mt-4">
                Verileriniz güvende • Hiçbir bilgi saklanmaz
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {/* Results Header */}
              <div className="text-center mb-8">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">
                  Analiz Tamamlandı!
                </h3>
                <p className="text-zinc-400">
                  <span className="text-white font-medium">{results.domain}</span> için sonuçlar
                </p>
              </div>

              {/* Overall Score */}
              <div className="flex justify-center mb-8">
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#27272A"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="url(#gradient)"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${results.overallScore * 3.52} 352`}
                      strokeLinecap="round"
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#8B5CF6" />
                        <stop offset="100%" stopColor="#EC4899" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-white">{results.overallScore}</span>
                    <span className="text-xs text-zinc-500">/100</span>
                  </div>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {results.metrics.map((metric, index) => (
                  <div key={index} className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                    <metric.icon className="w-5 h-5 text-violet-400 mb-2" />
                    <div className="text-2xl font-bold text-white mb-1">{metric.score}</div>
                    <div className="text-xs text-zinc-500">{metric.name}</div>
                  </div>
                ))}
              </div>

              {/* Potential Growth */}
              <div className="p-6 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-2xl border border-emerald-500/20 mb-8 text-center">
                <TrendingUp className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-400 mb-1">Potansiyel Büyüme</p>
                <p className="text-4xl font-bold text-emerald-400">{results.potentialGrowth}</p>
                <p className="text-xs text-zinc-500 mt-1">Optimizasyon sonrası tahmini CVR artışı</p>
              </div>

              {/* Key Recommendations */}
              <div className="mb-8">
                <h4 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">
                  Öncelikli Öneriler
                </h4>
                <ul className="space-y-2">
                  {results.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm">
                      <span className="w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-xs flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-zinc-300">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/iletisim"
                  className="px-8 py-4 bg-gradient-to-r from-violet-500 to-pink-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  Detaylı Analiz İçin Görüşelim
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <button
                  onClick={resetAnalysis}
                  className="px-8 py-4 border border-zinc-700 text-zinc-300 font-medium rounded-xl hover:border-zinc-500 transition-colors"
                >
                  Başka Site Analiz Et
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default LeadMagnet;
